
import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { FileCheck } from 'lucide-react';

interface GeneratedLink {
  url: string;
  id: string;
  type: string;
  timestamp: string;
}

interface GeneratedLinksPanelProps {
  generatedLinks: GeneratedLink[];
}

const GeneratedLinksPanel: React.FC<GeneratedLinksPanelProps> = ({ generatedLinks }) => {
  const { toast } = useToast();

  return (
    <ScrollArea className="flex-1 p-4">
      <h3 className="text-lg font-medium text-white mb-4">Generierte Links</h3>
      
      {generatedLinks.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FileCheck className="h-12 w-12 mx-auto mb-2 opacity-20" />
          <p>Keine Links generiert</p>
          <p className="text-sm mt-1">Erstellen Sie ein Angebot, um Links zu generieren</p>
        </div>
      ) : (
        <div className="space-y-3">
          {generatedLinks.map((link) => (
            <Card key={link.id} className="p-3 bg-black/40 border-white/10 backdrop-blur-sm shadow-lg">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={link.type === 'offer' ? 'default' : 'secondary'} className={`text-xs ${link.type === 'offer' ? 'bg-jade text-white' : ''}`}>
                      {link.type === 'offer' ? 'Angebot' : 'Vergleich'}
                    </Badge>
                    <span className="text-xs text-gray-400">
                      {new Date(link.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-200 break-all">{link.url}</p>
                </div>
                <div className="flex gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText(link.url);
                            toast({
                              title: "Link kopiert",
                              description: "Der Link wurde in die Zwischenablage kopiert.",
                            });
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black border-gray-800">
                        <p>Link kopieren</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0"
                          onClick={() => window.open(link.url, '_blank')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black border-gray-800">
                        <p>Link öffnen</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </ScrollArea>
  );
};

export default GeneratedLinksPanel;
